import { useState } from 'react';
import PageHero from '@/components/feature/PageHero';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { coursesHero, coursesList, courseCategories, courseStats, courseFAQ } from '@/mocks/courses';

export default function CoursesPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const filtered = activeCategory === 'all'
    ? coursesList
    : coursesList.filter((c) => c.category === activeCategory);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        title={coursesHero.title}
        subtitle={coursesHero.subtitle}
        imageUrl={coursesHero.imageUrl}
        breadcrumb={{ label: 'Home', href: '/' }}
      />

      {/* Stats */}
      <section className="py-8 md:py-10 bg-background-50 border-b border-background-200/70">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {courseStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 md:py-10 bg-background-50">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 justify-center">
            {courseCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="pb-14 md:pb-18">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((course) => (
              <article key={course.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden group transition-all duration-300 hover:border-primary-300/50">
                <div className="flex flex-col md:flex-row">
                  <div className="w-full md:w-48 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={course.imageUrl}
                      alt={course.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-900 text-xs font-medium whitespace-nowrap">
                        {course.level}
                      </span>
                      <span className="text-xs text-foreground-500">{course.duration}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-1">
                      {course.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-foreground-600 mb-3">
                      <i className="ri-calendar-line text-sm w-4 h-4 flex items-center justify-center"></i>
                      <span>{course.schedule}</span>
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4 flex-1">
                      {course.description}
                    </p>
                    <div>
                      <p className="text-xs font-medium text-foreground-500 mb-2 uppercase tracking-wide">Topics Covered</p>
                      <div className="flex flex-wrap gap-1.5">
                        {course.topics.map((topic, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-background-100 border-t border-background-200/70">
        <div className="px-4 md:px-6 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {courseFAQ.map((faq, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="font-medium text-foreground-950 text-sm md:text-base pr-4">{faq.question}</span>
                  <i className={`text-lg text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-primary-500">
        <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-background-50/80 text-base mb-8 leading-relaxed">
            All our courses are completely free. Visit our center or contact us to register for the next intake.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:insanperwirapenang@gmail.com"
              className="px-6 py-3 rounded-full bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-all whitespace-nowrap"
            >
              Register Now
            </a>
            <a
              href="https://wa.me/6041234567"
              target="_blank"
              rel="nofollow"
              className="px-6 py-3 rounded-full bg-primary-600 text-background-50 font-medium text-sm hover:bg-primary-700 transition-all whitespace-nowrap"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}